import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import useMedia from 'use-media';

import { ReactFCC, IconRegularShare } from '@notino/react-styleguide';

import { MAX_MEDIUM_BREAKPOINT } from '@components/Universals/Routine';
import { useOnClickOutside } from '@hooks/useOnClickOutside';

import { messages } from '../../messages';
import SharePopup from '../sharePopup/SharePopup';

import {
  DescriptionWrapper,
  PopupWrapper,
  ButtonWrapper,
  ButtonLabel,
  DescriptionLabel,
} from './styled';
import { calculatePositionForPopup } from './utils';

interface ShareButtonProps {
  direction: 'up' | 'down';
}

type Position = {
  top: number;
  left: number;
};

const ShareButtonComponent: ReactFCC<ShareButtonProps> = ({ direction }) => {
  const [popupPosition, setPopupPosition] = React.useState<Position | null>(
    null
  );
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMedia(MAX_MEDIUM_BREAKPOINT);

  const ref = useOnClickOutside(() => setPopupPosition(null), {
    enabled: !!popupPosition,
    exception: buttonRef.current ?? undefined,
  });

  const onClick = React.useCallback(async () => {
    if (isMobile) {
      await navigator.share?.({
        url: window.location.href,
      });
      return;
    }

    setPopupPosition((prev) =>
      prev
        ? null
        : buttonRef.current
        ? calculatePositionForPopup(buttonRef.current, direction)
        : null
    );
  }, [direction, isMobile]);

  return (
    <PopupWrapper>
      <DescriptionWrapper>
        <DescriptionLabel>
          <FormattedMessage {...messages.sharePopupLabel} />
        </DescriptionLabel>
        <ButtonWrapper ref={buttonRef} onClick={() => onClick()}>
          <IconRegularShare color="icon.primary" width="16px" height="16px" />
          <ButtonLabel>
            <FormattedMessage {...messages.shareButtonLabel} />
          </ButtonLabel>
        </ButtonWrapper>
      </DescriptionWrapper>

      {popupPosition && (
        <SharePopup
          direction={direction}
          innerRef={ref as React.RefObject<HTMLDivElement>}
          {...popupPosition}
        />
      )}
    </PopupWrapper>
  );
};

export default ShareButtonComponent;
